import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';
import licenseService from '../../../services/licenseService';
import { isLicenseSharingInviteNotification } from '../../../utils/notificationTypes';

const formatNotificationTime = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Agora';
  if (diffMinutes < 60) {
    return diffMinutes === 1 ? 'Há 1 minuto' : `Há ${diffMinutes} minutos`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return diffHours === 1 ? 'Há 1 hora' : `Há ${diffHours} horas`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return diffDays === 1 ? 'Ontem' : `Há ${diffDays} dias`;
  }

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTypePresentation = (type) => {
  switch (type) {
    case 'license_request':
      return {
        icon: 'ShieldCheck',
        iconWrap: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
        card: 'border-amber-200/80 dark:border-amber-800/50',
        badge: 'Solicitação de licença',
        badgeClass: 'text-amber-800 bg-amber-100/80 dark:text-amber-300 dark:bg-amber-950/40',
      };
    case 'DOCUMENT_SIGNED':
      return {
        icon: 'FileSignature',
        iconWrap: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
        card: 'border-emerald-200/80 dark:border-emerald-800/50',
        badge: 'Assinatura concluída',
        badgeClass: 'text-emerald-800 bg-emerald-100/80 dark:text-emerald-300 dark:bg-emerald-950/40',
      };
    default:
      return {
        icon: 'Bell',
        iconWrap: 'bg-primary/10 text-primary',
        card: 'border-border',
        badge: null,
        badgeClass: '',
      };
  }
};

const NotificationItem = ({
  notification,
  onMarkAsRead,
  onAction,
  onDelete,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isLicenseRequest = isLicenseSharingInviteNotification(notification);
  const isDocumentSigned = notification.type === 'DOCUMENT_SIGNED';
  const meta = notification.metadata || {};
  const documentLabel = meta.document_title || meta.documentTitle || null;
  const presentation = getTypePresentation(notification.type);
  const timeLabel = formatNotificationTime(notification.timestamp);
  const isUnread = !notification.isRead;

  const handleLicenseResponse = async (response) => {
    setLoading(true);
    setError('');

    try {
      await licenseService.handleLicenseResponse({
        owner_id: notification.metadata.owner_id,
        response,
      });

      await onDelete({ allowProtected: true });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Erro ao processar resposta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <article
      className={cn(
        'flex flex-col sm:flex-row gap-4 p-4 sm:p-5 rounded-xl border bg-card shadow-elevation-sm transition-colors',
        presentation.card,
        isUnread && 'border-l-[3px] border-l-primary bg-primary/[0.03]'
      )}
    >
      <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
        <div
          className={cn(
            'flex items-center justify-center w-12 h-12 rounded-xl shrink-0',
            presentation.iconWrap
          )}
        >
          <Icon name={presentation.icon} size={24} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
            {presentation.badge && (
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wide',
                  presentation.badgeClass
                )}
              >
                {presentation.badge}
              </span>
            )}
            {timeLabel && (
              <time
                className="text-xs text-muted-foreground tabular-nums"
                dateTime={notification.timestamp}
              >
                {timeLabel}
              </time>
            )}
            {isUnread && (
              <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">
                Nova
              </span>
            )}
          </div>

          <h3 className="font-semibold text-foreground leading-snug mb-1">
            {notification.title}
          </h3>

          <p className="text-sm text-muted-foreground leading-relaxed mb-3">
            {notification.message}
          </p>

          {isLicenseRequest && (
            <p className="text-xs text-muted-foreground mb-3">
              Este convite só desaparece quando você aceitar ou recusar.
            </p>
          )}

          {documentLabel && isDocumentSigned && (
            <p className="text-sm text-foreground/90 mb-4 rounded-lg bg-muted/40 border border-border px-3 py-2">
              <span className="text-muted-foreground">Documento: </span>
              {documentLabel}
            </p>
          )}

          {error && (
            <p className="text-sm text-destructive mb-3">{error}</p>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            {isLicenseRequest ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={loading}
                  onClick={() => handleLicenseResponse(false)}
                >
                  Recusar
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  disabled={loading}
                  loading={loading}
                  onClick={() => handleLicenseResponse(true)}
                >
                  Aceitar
                </Button>
              </>
            ) : (
              <>
                {notification.actionButton && (
                  <Button
                    size="sm"
                    variant={notification.actionButton.variant === 'outline' ? 'outline' : 'default'}
                    iconName={notification.actionButton.icon}
                    iconPosition="left"
                    onClick={onAction}
                  >
                    {notification.actionButton.label}
                  </Button>
                )}
                {isUnread && (
                  <Button size="sm" variant="ghost" onClick={onMarkAsRead}>
                    Marcar como lida
                  </Button>
                )}
              </>
            )}

            {!isLicenseRequest && (
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-destructive sm:ml-auto"
                iconName="Trash2"
                iconPosition="left"
                onClick={onDelete}
              >
                Excluir
              </Button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default NotificationItem;
